import {
  HttpClient,
  HttpContext,
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpHeaders,
  HttpParams,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { isUndefined } from 'lodash';
import {
  from,
  Observable,
  of,
  retry,
  switchMap,
  throwError,
  TimeoutError,
  timer,
} from 'rxjs';
import { filter, map, tap, timeout } from 'rxjs/operators';
import { MessagingService } from '../../cross-team-shared/messaging/messaging.service';
import { BrowserControlService } from '../../cross-team-shared/misc-services/browser-control.service';
import { skipDefaultErrorMessagingToken } from '../../cross-team-shared/misc-services/resources/url-request.service';
import { TokenManagementService } from '../../cross-team-shared/misc-services/token-management.service';
import { WorkspaceConfigService } from '../../shared/misc-services/workspace-config.service';
import { TokenScope } from '../../team-apis/otterdam/token-typings';

export interface HeadersOrParams {
  [name: string]: string | string[];
}

export const resourcePrefixCacheKey = 'http-cache';

type ResponseType = 'arraybuffer' | 'blob' | 'json' | 'text';

export interface RequestOptions {
  body?: any;
  responseType?: ResponseType;
  headers?: HeadersOrParams;
  params?: HeadersOrParams;
  cache?: boolean;
  retryCount?: number;
  skipDefaultErrorMessaging?: boolean;
  customErrorMessage?: string;
  tokenScope?: TokenScope;
  ignoreFailures?: boolean;
  defaultValue?: any;
  timeout?: number; // in milliseconds
}

const defaultOptions: RequestOptions = {
  retryCount: 0,
  responseType: 'json',
};

export abstract class AbstractResource {
  protected browserControlService = inject(BrowserControlService);
  protected messagingService = inject(MessagingService);
  protected workspaceConfig = inject(WorkspaceConfigService);
  protected cachePrefix = resourcePrefixCacheKey;

  private httpClient = inject(HttpClient);
  private tokenService = inject(TokenManagementService);

  private cache!: Promise<Cache>;

  protected request$<T>(
    method: 'GET' | 'PUT' | 'POST' | 'PATCH' | 'DELETE',
    url: string,
    options?: RequestOptions,
  ): Observable<T> {
    this.openCacheIfUndefined();
    const requestOptions = { ...defaultOptions, ...options };
    const cache$ = from(this.getFromCache<T>(url, requestOptions));
    const request$ = this.sendRequest$<T>(method, url, requestOptions);
    return cache$.pipe(switchMap((cached) => (cached ? of(cached) : request$)));
  }

  protected async getToken(tokenScope: TokenScope, forceRefresh = false) {
    return this.tokenService.getToken(tokenScope, forceRefresh);
  }

  private sendRequest$<T>(
    method: 'GET' | 'PUT' | 'POST' | 'PATCH' | 'DELETE',
    url: string,
    options: RequestOptions,
  ): Observable<T> {
    return this.getToken$(options.tokenScope).pipe(
      switchMap((token) => {
        const headers = this.buildHeaders(options.headers, token);
        const request = this.buildRequest(method, url, headers, options);

        let request$ = this.httpClient.request<T>(request);
        request$ = includeTimeout(request$, options);
        request$ = includeRetry(request$, options);

        return request$.pipe(
          tap({ error: () => this.handleErrors(options) }),
          filter(isResponseType<T>),
          tap((response) => this.addToCache(request.url, response, options)),
          map((response) => response.body!),
        );
      }),
    );
  }

  private handleErrors(options: RequestOptions) {
    if (options.customErrorMessage) {
      this.messagingService.error(options.customErrorMessage);
    }
  }

  private getToken$(tokenScope?: TokenScope): Observable<string | undefined> {
    return tokenScope ? this.tokenService.getToken$(tokenScope) : of(undefined);
  }

  private buildRequest(
    method: string,
    url: string,
    headers: HttpHeaders,
    options: RequestOptions,
  ) {
    const { body, params, responseType } = options;
    return new HttpRequest(method, url, body, {
      headers,
      params: params && new HttpParams({ fromString: $.param(params) }),
      context: this.buildContext(options),
      responseType,
    });
  }

  private buildHeaders(initialHeaders?: HeadersOrParams, token?: string) {
    // request.headers in Angular are updated immutably
    let headers = new HttpHeaders(initialHeaders);
    headers = headers.append('Accept', 'application/json');
    token && (headers = headers.append('Authorization', 'Bearer ' + token));

    return headers;
  }

  private buildContext(options: RequestOptions) {
    const { customErrorMessage, skipDefaultErrorMessaging } = options;
    const skipDefault = !!customErrorMessage || !!skipDefaultErrorMessaging;

    const context = new HttpContext();
    context.set(skipDefaultErrorMessagingToken, skipDefault);
    return context;
  }

  private async getFromCache<T>(url: string, options: RequestOptions) {
    const cache = await this.cache;
    const response = await cache.match(this.getCacheKey(url));
    return response && this.getResponseBody<T>(response, options);
  }

  private async addToCache(
    url: string,
    response: HttpResponse<any>,
    options?: RequestOptions,
  ) {
    if (
      response.ok &&
      options &&
      options.cache &&
      isCachableResponseType(options)
    ) {
      const cache = await this.cache;
      // promise rejections i.e. cache is already full are ignored for now
      void cache.put(
        this.getCacheKey(url),
        prepareResponseBody(response, options),
      );
    }
  }

  private async getResponseBody<T>(
    response: Response,
    options: RequestOptions,
  ) {
    let body: T | ArrayBuffer | Blob | string | null;
    switch (options.responseType) {
      case 'arraybuffer':
        body = await response.arrayBuffer();
        break;

      case 'blob':
        body = await response.blob();
        break;

      case 'text':
        body = await response.text();
        break;

      case 'json':
      default:
        body = await response.json();
        break;
    }

    return body as T;
  }

  private openCacheIfUndefined() {
    if (isUndefined(this.cache)) {
      this.cache = this.browserControlService.openCaches(
        getCacheKey(this.workspaceConfig.projectId, this.cachePrefix),
      );
    }
  }

  private getCacheKey(url: string) {
    return url.split('?')[0];
  }
}

export function getCacheKey(projectId: number, prefix: string) {
  return `${prefix}-${projectId}`;
}

function includeRetry<T>(
  request$: Observable<NonNullable<T>>,
  options: RequestOptions,
) {
  return request$.pipe(
    retry({
      delay: (error: HttpErrorResponse, retryCount: number) => {
        const maxRetryCount = options.retryCount || 0;
        if (isRetryable(error) && retryCount <= maxRetryCount) {
          return timer(5000);
        } else {
          return throwError(() => error);
        }
      },
    }),
  );
}

function includeTimeout<T>(request$: Observable<T>, options: RequestOptions) {
  if (options.timeout) {
    return request$.pipe(timeout(options.timeout));
  }
  return request$;
}

function isResponseType<T>(
  response: HttpEvent<unknown>,
): response is HttpResponse<T> {
  return response.type === HttpEventType.Response;
}

function isCachableResponseType(options: RequestOptions) {
  const validTypes: ResponseType[] = ['arraybuffer', 'json'];
  return validTypes.includes(options.responseType!);
}

function prepareResponseBody(
  response: HttpResponse<any>,
  options: RequestOptions,
) {
  switch (options.responseType) {
    case 'arraybuffer':
      return new Response(response.body);

    case 'json':
      return new Response(JSON.stringify(response.body));

    default:
      throw new Error(`Invalid response type ${options.responseType}`);
  }
}

function isRetryable(error: HttpErrorResponse | TimeoutError) {
  if (error instanceof TimeoutError) {
    return true;
  }
  return error.status < 400 || error.status >= 500;
}
